// src/utils/resultadoLab.js
import dayjs from 'dayjs';
import { UNIDADE_LEGADO, valorParaTexto } from './numeroLab';
import { corDeltaLog } from './mathFormatters';

/**
 * Resultado de laboratório de um experimento: quais campos o formulário pede (do
 * protocolo), como o registro salvo volta para o formulário e qual entrada compara
 * com o modelo. Usado pelo card de Amostras e pelo detalhe das páginas de coleta.
 */

/**
 * Registros antigos guardavam só `coliformesTotaisNmp`/`ecoliNmp`; aqui viram o
 * formato atual (`bacterias[]`), em NMP/100mL — a única unidade da tela na época.
 */
export const normalizarResultadoLab = (lab) => {
    if (!lab) return null;
    if (lab.bacterias?.length > 0) return lab;
    if (lab.coliformesTotaisNmp != null || lab.ecoliNmp != null) {
        return {
            ...lab,
            bacterias: [
                { bacteriaId: null, bacteriaNome: lab.bacteria0Nome || 'Coliformes Totais', nmp: lab.coliformesTotaisNmp, unit: UNIDADE_LEGADO },
                { bacteriaId: null, bacteriaNome: lab.bacteria1Nome || 'E. coli', nmp: lab.ecoliNmp, unit: UNIDADE_LEGADO },
            ],
        };
    }
    return lab;
};

/**
 * Uma entrada por (bactéria, unidade), espelhando o `calibrationSets` do protocolo.
 * A chave `${bacteriaId}|${unit}` impede os valores de se embaralharem quando a MESMA
 * bactéria aparece duas vezes (uma por unidade — o caso do Efluente).
 *
 * Protocolo sem `calibrationSets`: as duas entradas históricas em NMP/100mL, para
 * nenhuma amostra antiga mudar de comportamento.
 */
export const entradasLabDoProtocolo = (protocolo) => {
    const vistas = new Set();
    const entradas = [];
    (protocolo?.calibrationSets || []).forEach((cs) => {
        if (!cs?.bacteriaId) return;
        const unit = cs.unit || UNIDADE_LEGADO;
        const chave = `${cs.bacteriaId}|${unit}`;
        if (vistas.has(chave)) return;
        vistas.add(chave);
        entradas.push({ chave, bacteriaId: cs.bacteriaId, bacteriaNome: cs.bacteriaNome || cs.bacteriaId, unit });
    });
    if (entradas.length > 0) return entradas;
    return [
        { chave: `legado0|${UNIDADE_LEGADO}`, bacteriaId: null, bacteriaNome: 'Colif.', unit: UNIDADE_LEGADO },
        { chave: `legado1|${UNIDADE_LEGADO}`, bacteriaId: null, bacteriaNome: 'E. coli', unit: UNIDADE_LEGADO },
    ];
};

/** Entradas agrupadas por bactéria, na ordem do protocolo. */
export const agruparEntradasLab = (entradas) => {
    const porBacteria = new Map();
    entradas.forEach((e) => {
        const k = e.bacteriaId || e.bacteriaNome;
        if (!porBacteria.has(k)) {
            porBacteria.set(k, { bacteriaId: e.bacteriaId, bacteriaNome: e.bacteriaNome, entradas: [] });
        }
        porBacteria.get(k).entradas.push(e);
    });
    return Array.from(porBacteria.values());
};

/**
 * Valores do formulário a partir do registro salvo. Casa por (bacteriaId, unidade) e
 * cai para a POSIÇÃO quando o registro é legado (sem unit e, nos mais antigos, sem
 * bacteriaId) — sem isso um resultado já lançado voltaria em branco.
 */
export const valoresIniciaisLab = (entradas, labResult) => {
    const salvos = labResult?.bacterias || [];
    const valores = {};
    entradas.forEach((e, i) => {
        const casado =
            salvos.find((b) => b?.bacteriaId === e.bacteriaId && (b?.unit || UNIDADE_LEGADO) === e.unit) ||
            (salvos[i] && !salvos[i].unit ? salvos[i] : null);
        valores[e.chave] = valorParaTexto(casado?.nmp);
    });
    return { ...valores, analysisDate: labResult?.analysisDate || dayjs().format('YYYY-MM-DD') };
};

/**
 * Entrada do laboratório comparada com o modelo numa página de coleta: a bactéria
 * configurada pelo ADMIN, na unidade da página. Sem bactéria configurada, compara só
 * se houver UMA bactéria nessa unidade — com duas, qual comparar seria um chute.
 */
export const entradaLabDaPagina = (labResult, { bacteriaId, unidade } = {}) => {
    const prefixo = String(unidade || '').toUpperCase();
    const candidatas = (labResult?.bacterias || []).filter(
        (b) => Number.isFinite(b?.nmp) && String(b?.unit || '').toUpperCase().startsWith(prefixo)
    );
    if (bacteriaId) return candidatas.find((b) => b.bacteriaId === bacteriaId) || null;
    const bacterias = new Set(candidatas.map((b) => b.bacteriaId || b.bacteriaNome));
    return bacterias.size === 1 ? candidatas[0] : null;
};

/**
 * Laboratório × modelo, em log10 (a régua do CRM: utils/mathFormatters.corDeltaLog).
 * `null` quando falta um dos lados — contagem zero não tem log.
 */
export const compararComModelo = (log10Modelo, valorLab) => {
    if (!Number.isFinite(log10Modelo) || !Number.isFinite(valorLab) || valorLab <= 0) return null;
    const log10Lab = Math.log10(valorLab);
    const delta = log10Modelo - log10Lab;
    return { log10Lab, delta, cor: corDeltaLog(delta) };
};
